import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ContentCopyOutlined } from '@mui/icons-material';
import OtpCode from '@/components/invitation-flow/otp-code';
import { useNotifications } from '@/hooks/use-notifications';

export type AgentPairDeviceDialogLabels = {
  title: string;
  pairingCodeHint: string;
  pair: string;
  pairing: string;
  cancel: string;
  tokenHint: string;
  invalidCode: string;
  pairSuccess: string;
  pairError: string;
  copied: string;
  copyToken: string;
};

type AgentPairDeviceDialogProps = {
  open: boolean;
  playerId: string;
  labels: AgentPairDeviceDialogLabels;
  isPairing: boolean;
  onClose: () => void;
  onPair: (params: { playerId: string; code: string }) => Promise<{ token: string }>;
};

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

export default function AgentPairDeviceDialog({
  open,
  playerId,
  labels,
  isPairing,
  onClose,
  onPair,
}: AgentPairDeviceDialogProps) {
  const notification = useNotifications();
  const [pairCode, setPairCode] = useState('');
  const [pairedToken, setPairedToken] = useState<string | null>(null);
  const [otpKey, setOtpKey] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPairCode('');
    setPairedToken(null);
    setOtpKey((k) => k + 1);
  }, [open, playerId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handlePair = useCallback(async () => {
    if (!/^\d{6}$/.test(pairCode)) {
      notification.notifyError(labels.invalidCode);
      return;
    }
    try {
      const result = await onPair({ playerId, code: pairCode });
      setPairedToken(result.token);
      notification.notifySuccess(labels.pairSuccess);
    } catch {
      notification.notifyError(labels.pairError);
    }
  }, [labels.invalidCode, labels.pairError, labels.pairSuccess, notification, onPair, pairCode, playerId]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{labels.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {labels.pairingCodeHint}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {playerId}
          </Typography>
          <OtpCode key={otpKey} autoFocus onChange={setPairCode} onConfirm={() => void handlePair()} />
          {pairedToken && (
            <Alert
              severity="success"
              action={
                <IconButton
                  size="small"
                  aria-label={labels.copyToken}
                  onClick={() => void copyText(pairedToken).then(() => notification.notifySuccess(labels.copied))}
                >
                  <ContentCopyOutlined fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {labels.tokenHint}
              </Typography>
              <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                WEBSOCKET_TOKEN={pairedToken}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{labels.cancel}</Button>
        <Button variant="contained" onClick={() => void handlePair()} disabled={isPairing || pairCode.length !== 6}>
          {isPairing ? <CircularProgress size={20} color="inherit" /> : labels.pair}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
